import { signal, computed, effect } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { QueryObserver } from '@tanstack/query-core';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import { EmptyObject } from '@thinkalpha/common/util/empty.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { toDisposable } from '@thinkalpha/ui-core/toDisposable';
import { cn } from '@thinkalpha/ui-core/utils';
import debounce from 'lodash.debounce';
import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useSvelteComponent } from 'src/hooks/svelte/useSvelteComponent';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'InfiniteTimeView' });

export type DataRow = {
    id: string;
    type: 'data';
    isEndOfDataUpward?: boolean;
    isEndOfDataDownward?: boolean;
    parity?: 'odd' | 'even';
};

export type DecorationRow = {
    id: string;
    type: 'decoration';
};

export type BufferRow = {
    type: 'buffer';
    id: string;
    position: 'top' | 'bottom';
};

export type Row<T extends DataRow = DataRow, U extends DecorationRow = DecorationRow> = T | U | BufferRow;

export type FetchMoreResult<T extends DataRow> = {
    rows: T[];
    isEndOfDataUpward?: boolean;
    isEndOfDataDownward?: boolean;
};

export interface RowDataSourceState<T extends DataRow = DataRow, U extends DecorationRow = DecorationRow, V = unknown> {
    topBuffer: number;
    data: (T | U)[];
    bottomBuffer: number;
    scrollTop: number;
    clientHeight: number;
    isFetchingUp: boolean;
    isFetchingDown: boolean;
    hasReachedStart: boolean;
    hasReachedEnd: boolean;
    viewportShift: number;
    rowHeight: number;
    visibleRange: {
        start: number;
        end: number;
    };
    manualFetchUpEnqueued: boolean;
    initialFetchParameter: V | null;
}

export type RowDataSourceQueryResult<T extends DataRow = DataRow> =
    | { type: 'INITIAL_FETCH_SUCCESS'; rows: T[]; isEndOfDataUpward?: boolean; isEndOfDataDownward?: boolean }
    | { type: 'INITIAL_FETCH_FAILURE' }
    | { type: 'FETCH_UP_SUCCESS'; rows: T[]; isEndOfData?: boolean }
    | { type: 'FETCH_UP_FAILURE' }
    | { type: 'FETCH_DOWN_SUCCESS'; rows: T[]; isEndOfData?: boolean }
    | { type: 'FETCH_DOWN_FAILURE' }
    | { type: 'INSERT_ROWS'; rows: T[] };

export type RowDataSourceQueryKey = [
    prefix: 'ROW_DATA_SOURCE',
    // The type of operation being performed, this will be read to determine how to propagate the fetching state through the reducer
    operation: 'INITIAL_FETCH' | 'FETCH_UP' | 'FETCH_DOWN' | 'OTHER',
    ...unknown[],
];

export interface RowDataSourceConstructionOptions<
    T extends DataRow = DataRow,
    U extends DecorationRow = DecorationRow,
    V = unknown,
> {
    rowHeight: number;
    bufferSize: number;
    overscanRows: number;
    viewportDebounceDelay: number;
    comparator: (a: T, b: T) => number;
    decoratorFunction: DecoratorFunction<T, U>;
    queryOptionsFactory: (
        stateSignal: ReadonlySignal<RowDataSourceState<T, U, V>>,
    ) => ReadonlySignal<QueryObserverOptions<any, Error, RowDataSourceQueryResult<T>, RowDataSourceQueryKey>>;
    queryClient: QueryClient;
    initialFetchParameter?: V;
}

export type Action<T extends DataRow = DataRow> =
    | { type: 'VIEWPORT_CHANGED'; scrollTop: number; clientHeight: number }
    | { type: 'INITIAL_FETCH_START' }
    | { type: 'INITIAL_FETCH_SUCCESS'; rows: T[]; isEndOfDataUpward?: boolean; isEndOfDataDownward?: boolean }
    | { type: 'INITIAL_FETCH_FAILURE' }
    | { type: 'FETCH_UP_START' }
    | { type: 'FETCH_UP_SUCCESS'; rows: T[]; isEndOfData?: boolean }
    | { type: 'FETCH_UP_FAILURE' }
    | { type: 'FETCH_DOWN_START' }
    | { type: 'FETCH_DOWN_SUCCESS'; rows: T[]; isEndOfData?: boolean }
    | { type: 'FETCH_DOWN_FAILURE' }
    | { type: 'MARK_END_OF_DATA'; direction: 'up' | 'down' }
    /**
     * @deprecated This was being used to fetch live data, use `ENQUEUE_MANUAL_FETCH_UP` instead.
     */
    | { type: 'RESET_END_STATE'; direction?: 'up' | 'down' | 'both' }
    | { type: 'INSERT_ROWS'; rows: T[]; skipViewportShift?: boolean }
    | {
          type: 'REPLACE_ROWS';
          rows: T[];
          isEndOfDataUpward: boolean;
          isEndOfDataDownward: boolean;
      }
    | { type: 'UPSERT_ROWS'; rows: T[]; updater: (newRow: T, existingRow: T | undefined) => T }
    | { type: 'REMOVE_ROWS_BY_ID'; rowIds: string[]; skipViewportShift?: boolean }
    | { type: 'ENQUEUE_MANUAL_FETCH_UP' };

function calculateVisibleRange<T extends DataRow, V = unknown>(
    state: RowDataSourceState<T, DecorationRow, V>,
    overscanRows: number,
): { start: number; end: number } {
    const { scrollTop, clientHeight, rowHeight, topBuffer, data, bottomBuffer } = state;
    const totalRows = topBuffer + data.length + bottomBuffer;
    const startRow = Math.floor(scrollTop / rowHeight);
    const endRow = Math.ceil((scrollTop + clientHeight) / rowHeight) - 1;

    const start = Math.max(0, startRow - overscanRows);
    const end = Math.min(totalRows - 1, endRow + overscanRows);

    return { start, end };
}

/**
 * Decorator function that can be used to add decoration rows to the data set
 * @param rows - The two rows to decorate
 * @returns The decoration row to insert between the two rows, or undefined if no decoration row should be inserted
 */
export type DecoratorFunction<T extends DataRow, U extends DecorationRow> = (
    row1: T | undefined,
    row2: T | undefined,
) =>
    | {
          position: 'start' | 'between';
          row: U;
      }
    | undefined;

export const applyDecoratorFunctionToRows = <T extends DataRow, U extends DecorationRow>(
    rows: T[],
    decoratorFunction: DecoratorFunction<T, U>,
): (T | U)[] => {
    if (TRACE) {
        log.trace('applyDecoratorFunctionToRows called', {
            rowsCount: rows.length,
            decoratorFunction,
        });
    }

    const decoratedRows: (T | U)[] = [];

    if (rows.length === 0) {
        return decoratedRows;
    }

    for (let i = -1; i < rows.length; i++) {
        const currentRow = i >= 0 ? rows[i] : undefined;
        const nextRow = rows[i + 1];

        const decoratorResult = decoratorFunction(currentRow, nextRow);

        if (decoratorResult !== undefined) {
            if (decoratorResult.position === 'start') {
                // Insert decoration row at the start (before current row)
                decoratedRows.push(decoratorResult.row);
                if (currentRow) {
                    decoratedRows.push(currentRow);
                }
            } else {
                // Insert current row, then decoration row (between current and next)
                if (currentRow) {
                    decoratedRows.push(currentRow);
                }
                decoratedRows.push(decoratorResult.row);
            }
        } else {
            // No decoration row, just push the current row
            if (currentRow) {
                decoratedRows.push(currentRow);
            }
        }
    }

    return decoratedRows;
};

export function updateRows<T extends DataRow, U extends DecorationRow, V = unknown>(
    /**
     * The previous state, used to determine what changed
     */
    state: RowDataSourceState<T, U, V>,
    /**
     * Rows to add to the current data set
     */
    rowsToAdd: T[],
    rowsToRemove: T[],
    config: {
        bufferSize: number;
        overscanRows: number;
        comparator: (a: T | U, b: T | U) => number;
        decoratorFunction: DecoratorFunction<T, U>;
    },
    /**
     * Optional updater function for upsert operations. If provided, for each row in rowsToAdd:
     * - The updater is called with (newRow, existingRow | undefined)
     * - The updater's return value is stored in the data set
     * If not provided, rows in rowsToAdd simply overwrite existing rows by ID (current behavior)
     */
    updater?: (newRow: T, existingRow: T | undefined) => T,
    /**
     * Explicit boundary state to use when a replacement confirms there are no data rows.
     * Empty data cannot carry boundary markers, so the query result must supply this state.
     */
    emptyDataBoundaries?: Pick<RowDataSourceState<T, U, V>, 'hasReachedStart' | 'hasReachedEnd'>,
): RowDataSourceState<T, U, V> {
    if (TRACE) {
        log.trace('updateRows called', {
            rowsToAddCount: rowsToAdd.length,
            existingDataCount: state.data.length,
            scrollTop: state.scrollTop,
            clientHeight: state.clientHeight,
            visibleRange: state.visibleRange,
        });
    }

    // Start and end rows depend on the client scrollTop
    const startRow = Math.floor(state.scrollTop / state.rowHeight);
    const rowsVisible = Math.ceil(state.clientHeight / state.rowHeight);
    const overscan = config.overscanRows;

    // The visible data rows are offset by the number of rows in the top buffer.
    const dataStartIndex = state.topBuffer;
    const visibleDataStart = Math.max(0, startRow - dataStartIndex);

    // Find the ID of the first visible data row (if any) before merging
    let firstVisibleRowId: string | null = null;
    // Only track a data row if we're actually looking at the data area, not the buffer area
    if (startRow >= dataStartIndex && state.data.length > 0 && visibleDataStart < state.data.length) {
        firstVisibleRowId = state.data[visibleDataStart].id;
    }

    // Remove duplicates by ID (keeping the newer version from rowsToAdd if it exists)
    const uniqueRowsMap = new Map<string, T>();
    // First add existing data
    for (const row of state.data) {
        if (row.type === 'decoration') continue; // strip out decoration rows
        if (rowsToRemove.includes(row)) continue; // remove the rows marked for removal
        uniqueRowsMap.set(row.id, row);
    }
    // Then process new rows with optional updater
    if (updater) {
        // Upsert mode: use updater callback
        for (const newRow of rowsToAdd) {
            const existingRow = uniqueRowsMap.get(newRow.id);
            const finalRow = updater(newRow, existingRow);
            uniqueRowsMap.set(finalRow.id, finalRow);
        }
    } else {
        // Insert mode: overwrite with new rows (current behavior)
        for (const row of rowsToAdd) {
            uniqueRowsMap.set(row.id, row);
        }
    }

    const sortedData = Array.from(uniqueRowsMap.values());
    sortedData.sort(config.comparator);

    // After sorting, check if any row with end markers is in the wrong position
    if (sortedData.length > 0) {
        // Remove isEndOfDataUpward from any row that's not the first
        for (let i = 1; i < sortedData.length; i++) {
            if (sortedData[i].isEndOfDataUpward) {
                // This row has the upward end marker but is not the first row anymore
                sortedData[i] = { ...sortedData[i] };
                delete sortedData[i].isEndOfDataUpward;
            }
        }

        // Remove isEndOfDataDownward from any row that's not the last
        for (let i = 0; i < sortedData.length - 1; i++) {
            if (sortedData[i].isEndOfDataDownward) {
                // This row has the downward end marker but is not the last row anymore
                sortedData[i] = { ...sortedData[i] };
                delete sortedData[i].isEndOfDataDownward;
            }
        }

        // If state indicates we've reached an end, ensure the first/last row has the marker
        // This handles the case where socket updates come in without end markers but state has reached ends
        // The only exception is when we're removing the boundary row itself (which invalidates our knowledge)

        const existingDataRows = state.data.filter((r) => r.type === 'data');
        const existingFirstRow = existingDataRows[0];
        const existingLastRow = existingDataRows[existingDataRows.length - 1];

        // Check if we're removing rows that had end markers
        // When we remove a boundary row, we lose knowledge of whether we're still at the boundary
        const removingFirstRowWithMarker =
            rowsToRemove.length > 0 &&
            existingFirstRow &&
            rowsToRemove.some((r) => r.id === existingFirstRow.id && existingFirstRow.isEndOfDataUpward);
        const removingLastRowWithMarker =
            rowsToRemove.length > 0 &&
            existingLastRow &&
            rowsToRemove.some((r) => r.id === existingLastRow.id && existingLastRow.isEndOfDataDownward);

        // Add end markers based on state flags
        // If hasReachedStart is true, the first row should have the upward marker (unless we just removed it)
        // If hasReachedEnd is true, the last row should have the downward marker (unless we just removed it)
        if (state.hasReachedStart && !removingFirstRowWithMarker && !sortedData[0].isEndOfDataUpward) {
            sortedData[0] = { ...sortedData[0], isEndOfDataUpward: true };
        }

        if (
            state.hasReachedEnd &&
            !removingLastRowWithMarker &&
            !sortedData[sortedData.length - 1].isEndOfDataDownward
        ) {
            sortedData[sortedData.length - 1] = { ...sortedData[sortedData.length - 1], isEndOfDataDownward: true };
        }
    }

    // Apply zebra striping parity to sorted data
    if (sortedData.length > 0) {
        let anchorIndex = -1;
        let anchorParity: 'odd' | 'even' | undefined;

        // find first row in sortedData that has parity
        const firstRowWithParity = sortedData.find((row) => row.parity !== undefined);
        if (firstRowWithParity) {
            anchorIndex = sortedData.indexOf(firstRowWithParity);
            anchorParity = firstRowWithParity.parity;
        }

        // If we don't have a row with parity, start with 'odd' for the first row
        if (anchorParity === undefined) {
            anchorParity = 'odd';
            anchorIndex = 0;
        }

        // Apply parity to all rows based on the anchor
        for (let i = 0; i < sortedData.length; i++) {
            const distanceFromAnchor = i - anchorIndex;
            // Calculate parity based on distance from anchor
            // If anchor is 'odd' and we're 2 rows after it, we should also be 'odd'
            // If anchor is 'even' and we're 1 row after it, we should be 'odd'
            // Note: We use distanceFromAnchor directly (not Math.abs) to ensure
            // rows at different positions get different parity values
            const isEvenDistance = distanceFromAnchor % 2 === 0;
            const parity = isEvenDistance ? anchorParity : anchorParity === 'odd' ? 'even' : 'odd';

            sortedData[i] = { ...sortedData[i], parity };
        }

        if (TRACE) {
            log.trace('Applied parity to rows', {
                firstRowId: sortedData[0]?.id,
                firstRowParity: sortedData[0]?.parity,
                totalRows: sortedData.length,
                anchorIndex,
                anchorParity,
            });
        }
    }

    const decoratedRows = applyDecoratorFunctionToRows(sortedData, config.decoratorFunction);
    const lastDecoratedRowIndex = Math.max(0, decoratedRows.length - 1);

    // Find where the first visible row ended up after sorting and applying the decorator function
    let newVisibleDataStart = 0;
    if (firstVisibleRowId !== null) {
        const index = decoratedRows.findIndex((row) => row.id === firstVisibleRowId);
        newVisibleDataStart = index !== -1 ? index : Math.min(visibleDataStart, lastDecoratedRowIndex);
    } else if (startRow >= dataStartIndex) {
        // We were viewing a position in the data area but had no visible row
        // This happens when scrolled beyond current data
        // Try to maintain the same position after merge
        newVisibleDataStart = Math.min(startRow - dataStartIndex, lastDecoratedRowIndex);
    }

    // Calculate how many rows were inserted before the first visible row
    let rowsInsertedBefore = 0;

    if (state.scrollTop < state.topBuffer * state.rowHeight) {
        // We're viewing the top buffer area - ALL new rows that sort before
        // the existing data are inserted "above" our view
        if (state.data.length > 0) {
            const firstExistingRow = state.data[0];
            rowsInsertedBefore = Math.max(
                0,
                decoratedRows.findIndex((row) => row.id === firstExistingRow.id),
            );
        } else {
            // No existing data, so no rows are "before" anything
            rowsInsertedBefore = 0;
        }
    } else if (firstVisibleRowId !== null) {
        // We had a visible data row, calculate based on its new position
        rowsInsertedBefore = newVisibleDataStart - visibleDataStart;
    }

    // We keep data according to what's visible plus overscan
    // If we're in the buffer area, we need to adjust our keep range
    let adjustedVisibleStart = newVisibleDataStart;
    if (state.scrollTop < state.topBuffer * state.rowHeight && rowsInsertedBefore > 0) {
        // We're in the buffer area and rows were inserted before our view
        // We need to keep rows starting from where our viewport would be after insertion
        const currentRow = Math.floor(state.scrollTop / state.rowHeight);
        const rowsInTopBuffer = state.topBuffer - currentRow;
        adjustedVisibleStart = rowsInsertedBefore - rowsInTopBuffer;
    }

    const keepStart = Math.max(0, adjustedVisibleStart - overscan);
    const keepEnd = Math.min(decoratedRows.length - 1, keepStart + rowsVisible + overscan * 2);

    const keptRows = decoratedRows.slice(keepStart, keepEnd + 1);

    if (TRACE) {
        const keptDataRowsDebug = keptRows.filter((r) => r.type === 'data');
        log.trace('Kept rows after slicing', {
            keptRowsCount: keptRows.length,
            firstKeptDataRowId: keptDataRowsDebug[0]?.id,
            firstKeptDataRowParity: keptDataRowsDebug[0]?.parity,
            keepStart,
            keepEnd,
        });
    }

    // Check end markers in the keptDataRows (after windowing)
    // we need to ignore decoration rows when looking for end markers
    const keptDataRows = keptRows.filter((row) => row.type === 'data');
    const hasReachedStart =
        keptDataRows.length > 0
            ? Boolean(keptDataRows[0].isEndOfDataUpward)
            : (emptyDataBoundaries?.hasReachedStart ?? false);
    const hasReachedEnd =
        keptDataRows.length > 0
            ? Boolean(keptDataRows[keptDataRows.length - 1].isEndOfDataDownward)
            : (emptyDataBoundaries?.hasReachedEnd ?? false);

    const topBuffer = hasReachedStart ? 0 : config.bufferSize;
    const bottomBuffer = hasReachedEnd ? 0 : config.bufferSize;

    // Check if we can return early (no changes needed)
    // This needs to check if the kept data matches the existing state data exactly
    if (
        rowsToAdd.length === 0 &&
        keptRows.length === state.data.length &&
        keptRows.every((row, i) => row.id === state.data[i].id) &&
        state.topBuffer === topBuffer &&
        state.bottomBuffer === bottomBuffer
    ) {
        return state;
    }

    // Calculate viewport shift
    let viewportShift: number;

    // Special case: If user is at absolute top (scrollTop = 0 and no top buffer),
    // and we're adding new rows, don't apply viewport shift so new content appears at top
    if (state.scrollTop === 0 && state.topBuffer === 0 && rowsInsertedBefore > 0) {
        viewportShift = 0;
    } else {
        // Normal viewport shift calculation
        let rowsRemovedFromTop = keepStart - rowsInsertedBefore;
        if (topBuffer !== state.topBuffer) {
            rowsRemovedFromTop += state.topBuffer - topBuffer;
        }
        viewportShift = state.viewportShift - rowsRemovedFromTop * state.rowHeight;
    }

    return {
        ...state,
        data: keptRows,
        topBuffer,
        bottomBuffer,
        hasReachedStart,
        hasReachedEnd,
        viewportShift,
    };
}

export function reducer<T extends DataRow, U extends DecorationRow, V = unknown>(
    state: RowDataSourceState<T, U, V>,
    action: Action<T>,
    config: {
        bufferSize: number;
        overscanRows: number;
        comparator: (a: T | U, b: T | U) => number;
        decoratorFunction: DecoratorFunction<T, U>;
    },
): RowDataSourceState<T, U, V> {
    switch (action.type) {
        case 'VIEWPORT_CHANGED': {
            if (
                // If there's currently a viewport shift pending, this action applies or overrides it
                !state.viewportShift &&
                // Otherwise we only care about actual changes
                action.scrollTop === state.scrollTop &&
                action.clientHeight === state.clientHeight
            ) {
                return state;
            }

            const newState = {
                ...state,
                scrollTop: action.scrollTop,
                clientHeight: action.clientHeight,
                viewportShift: 0,
            };

            return {
                ...newState,
                visibleRange: calculateVisibleRange(newState, config.overscanRows),
            };
        }

        case 'INITIAL_FETCH_START':
            return {
                ...state,
                isFetchingDown: true,
            };

        case 'INITIAL_FETCH_SUCCESS': {
            let rows = action.rows;

            // Handle isEndOfData flags
            if (action.isEndOfDataUpward && rows.length > 0) {
                rows = [{ ...rows[0], isEndOfDataUpward: true }, ...rows.slice(1)];
            }
            if (action.isEndOfDataDownward && rows.length > 0) {
                rows = [...rows.slice(0, -1), { ...rows[rows.length - 1], isEndOfDataDownward: true }];
            }

            const hasUpwardEnd = action.isEndOfDataUpward || rows.some((r) => r.isEndOfDataUpward);
            const hasDownwardEnd = action.isEndOfDataDownward || rows.some((r) => r.isEndOfDataDownward);

            // Apply parity to all rows (starting from index 0 with 'odd')
            // Since this is initial fetch, we always start fresh with no anchor
            const rowsWithParity = rows.map((row, i) => ({
                ...row,
                parity: (i % 2 === 0 ? 'odd' : 'even') as 'odd' | 'even',
            }));

            // Apply decorator function
            const decoratedRows = applyDecoratorFunctionToRows(rowsWithParity, config.decoratorFunction);

            const newState = {
                ...state,
                isFetchingDown: false,
                topBuffer: hasUpwardEnd ? 0 : config.bufferSize,
                data: decoratedRows,
                bottomBuffer: hasDownwardEnd ? 0 : config.bufferSize,
                hasReachedStart: hasUpwardEnd,
                hasReachedEnd: hasDownwardEnd,
                initialFetchParameter: null,
            };

            return {
                ...newState,
                visibleRange: calculateVisibleRange(newState, config.overscanRows),
            };
        }

        case 'INITIAL_FETCH_FAILURE':
            return {
                ...state,
                isFetchingDown: false,
            };

        case 'FETCH_UP_START':
            return {
                ...state,
                isFetchingUp: true,
                manualFetchUpEnqueued: false,
            };

        case 'FETCH_UP_SUCCESS': {
            let newState = {
                ...state,
                isFetchingUp: false,
            };

            // If isEndOfData is true and no rows returned, mark the first existing data row
            if (action.isEndOfData && action.rows.length === 0 && newState.data.length > 0) {
                const updatedData = [...newState.data];
                const firstDataRowIndex = updatedData.findIndex((row) => row.type === 'data');
                if (firstDataRowIndex !== -1) {
                    updatedData[firstDataRowIndex] = { ...updatedData[firstDataRowIndex], isEndOfDataUpward: true };
                }
                newState = {
                    ...newState,
                    data: updatedData,
                    hasReachedStart: true,
                    topBuffer: 0,
                };
                return {
                    ...newState,
                    visibleRange: calculateVisibleRange(newState, config.overscanRows),
                };
            }

            // If isEndOfData is true and rows returned, mark the first row
            const rowsToAdd =
                action.isEndOfData && action.rows.length > 0
                    ? [{ ...action.rows[0], isEndOfDataUpward: true }, ...action.rows.slice(1)]
                    : action.rows;

            const discardedState = updateRows(newState, rowsToAdd, [], config);
            return {
                ...discardedState,
                visibleRange: calculateVisibleRange(discardedState, config.overscanRows),
            };
        }

        case 'FETCH_UP_FAILURE':
            return {
                ...state,
                isFetchingUp: false,
            };

        case 'FETCH_DOWN_START':
            return {
                ...state,
                isFetchingDown: true,
            };

        case 'FETCH_DOWN_SUCCESS': {
            let newState = {
                ...state,
                isFetchingDown: false,
            };

            // If isEndOfData is true and no rows returned, mark the last existing data row
            if (action.isEndOfData && action.rows.length === 0 && newState.data.length > 0) {
                const updatedData = [...newState.data];
                const lastDataRowIndex = updatedData.map((row) => row.type).lastIndexOf('data');
                if (lastDataRowIndex !== -1) {
                    updatedData[lastDataRowIndex] = {
                        ...updatedData[lastDataRowIndex],
                        isEndOfDataDownward: true,
                    };
                }
                newState = {
                    ...newState,
                    data: updatedData,
                    hasReachedEnd: true,
                    bottomBuffer: 0,
                };
                return {
                    ...newState,
                    visibleRange: calculateVisibleRange(newState, config.overscanRows),
                };
            }

            // If isEndOfData is true and rows returned, mark the last row
            const rowsToAdd =
                action.isEndOfData && action.rows.length > 0
                    ? [
                          ...action.rows.slice(0, -1),
                          { ...action.rows[action.rows.length - 1], isEndOfDataDownward: true },
                      ]
                    : action.rows;

            const discardedState = updateRows(newState, rowsToAdd, [], config);
            return {
                ...discardedState,
                visibleRange: calculateVisibleRange(discardedState, config.overscanRows),
            };
        }

        case 'FETCH_DOWN_FAILURE':
            return {
                ...state,
                isFetchingDown: false,
            };

        case 'MARK_END_OF_DATA': {
            if (state.data.length === 0) {
                return state;
            }

            if (action.direction === 'up') {
                const updatedData = [...state.data];
                updatedData[0] = { ...updatedData[0], isEndOfDataUpward: true };
                return {
                    ...state,
                    data: updatedData,
                    hasReachedStart: true,
                    topBuffer: 0,
                };
            } else {
                const updatedData = [...state.data];
                updatedData[updatedData.length - 1] = {
                    ...updatedData[updatedData.length - 1],
                    isEndOfDataDownward: true,
                };
                return {
                    ...state,
                    data: updatedData,
                    hasReachedEnd: true,
                    bottomBuffer: 0,
                };
            }
        }

        case 'RESET_END_STATE': {
            const direction = action.direction || 'both';

            if (state.data.length === 0) {
                // Still reset the flags even with no data
                if (direction === 'up' || direction === 'both') {
                    return {
                        ...state,
                        hasReachedStart: false,
                        topBuffer: config.bufferSize,
                        viewportShift: state.viewportShift + config.bufferSize * state.rowHeight,
                        ...(direction === 'both'
                            ? {
                                  hasReachedEnd: false,
                                  bottomBuffer: config.bufferSize,
                              }
                            : {}),
                    };
                } else if (direction === 'down') {
                    return {
                        ...state,
                        hasReachedEnd: false,
                        bottomBuffer: config.bufferSize,
                    };
                }
            }

            // Clone data and remove end markers based on direction
            const updatedData = state.data.map((row) => {
                const newRow = { ...row };

                if ((direction === 'up' || direction === 'both') && 'isEndOfDataUpward' in newRow) {
                    delete newRow.isEndOfDataUpward;
                }

                if ((direction === 'down' || direction === 'both') && 'isEndOfDataDownward' in newRow) {
                    delete newRow.isEndOfDataDownward;
                }

                return newRow;
            });

            // Calculate new state based on direction
            const newState = { ...state, data: updatedData };

            if (direction === 'up' || direction === 'both') {
                newState.hasReachedStart = false;
                newState.topBuffer = config.bufferSize;
                newState.viewportShift = state.viewportShift + config.bufferSize * state.rowHeight;
            }

            if (direction === 'down' || direction === 'both') {
                newState.hasReachedEnd = false;
                newState.bottomBuffer = config.bufferSize;
            }

            return newState;
        }

        case 'INSERT_ROWS': {
            // Use updateRows to merge the new rows with existing data
            const mergedState = updateRows(state, action.rows, [], config);
            return {
                ...mergedState,
                viewportShift: action.skipViewportShift ? 0 : mergedState.viewportShift,
                visibleRange: calculateVisibleRange(mergedState, config.overscanRows),
            };
        }

        case 'REPLACE_ROWS': {
            const currentDataRows = state.data.filter((row): row is T => row.type === 'data');
            const currentParityById = new Map<string, T['parity']>();
            for (const row of currentDataRows) {
                currentParityById.set(row.id, row.parity);
            }
            const rowsWithMarkers = action.rows.map((row) => {
                const rowWithCurrentMarkers = { ...row };
                const currentParity = currentParityById.get(row.id);
                if (currentParity !== undefined) {
                    rowWithCurrentMarkers.parity = currentParity;
                }
                delete rowWithCurrentMarkers.isEndOfDataUpward;
                delete rowWithCurrentMarkers.isEndOfDataDownward;
                return rowWithCurrentMarkers;
            });
            if (action.isEndOfDataUpward && rowsWithMarkers.length > 0) {
                rowsWithMarkers[0] = { ...rowsWithMarkers[0], isEndOfDataUpward: true };
            }
            if (action.isEndOfDataDownward && rowsWithMarkers.length > 0) {
                const lastIndex = rowsWithMarkers.length - 1;
                rowsWithMarkers[lastIndex] = { ...rowsWithMarkers[lastIndex], isEndOfDataDownward: true };
            }

            const stateWithCurrentBoundaries = {
                ...state,
                hasReachedStart: action.isEndOfDataUpward,
                hasReachedEnd: action.isEndOfDataDownward,
            };
            const replacedState = updateRows(
                stateWithCurrentBoundaries,
                rowsWithMarkers,
                currentDataRows,
                config,
                undefined,
                {
                    hasReachedStart: action.isEndOfDataUpward,
                    hasReachedEnd: action.isEndOfDataDownward,
                },
            );
            return {
                ...replacedState,
                visibleRange: calculateVisibleRange(replacedState, config.overscanRows),
            };
        }

        case 'UPSERT_ROWS': {
            // Use updateRows with the updater callback for upsert logic
            const mergedState = updateRows(state, action.rows, [], config, action.updater);
            return {
                ...mergedState,
                visibleRange: calculateVisibleRange(mergedState, config.overscanRows),
            };
        }

        case 'REMOVE_ROWS_BY_ID': {
            if (TRACE) {
                log.trace('REMOVE_ROWS_BY_ID action', {
                    rowIdsToRemove: action.rowIds,
                    existingDataCount: state.data.length,
                });
            }
            const idsToRemove = new Set(action.rowIds);
            const rowsToRemove = state.data
                .filter((row) => row.type === 'data') // type guard for data rows
                .filter((row) => idsToRemove.has(row.id));
            const newState = updateRows(state, [], rowsToRemove, config);

            return {
                ...newState,
                viewportShift: action.skipViewportShift ? 0 : newState.viewportShift,
                visibleRange: calculateVisibleRange(newState, config.overscanRows),
            };
        }

        case 'ENQUEUE_MANUAL_FETCH_UP':
            return {
                ...state,
                manualFetchUpEnqueued: true,
            };

        default:
            return state;
    }
}

export class RowDataSource<T extends DataRow = DataRow, U extends DecorationRow = DecorationRow, V = unknown> {
    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private state = signal<RowDataSourceState<T, U, V>>({
        topBuffer: 0,
        data: [],
        bottomBuffer: 0,
        scrollTop: 0,
        clientHeight: 0,
        isFetchingUp: false,
        isFetchingDown: false,
        hasReachedStart: false,
        hasReachedEnd: false,
        viewportShift: 0,
        rowHeight: 24,
        visibleRange: { start: 0, end: 0 },
        manualFetchUpEnqueued: false,
        initialFetchParameter: null,
    });

    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private container: HTMLDivElement | null = null;

    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private config: RowDataSourceConstructionOptions<T, U, V>;

    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private resizeObserver: ResizeObserver | null = null;
    #reactiveEffectsBox = new DisposableBox();

    updateViewport: (scrollTop: number, clientHeight: number) => void;

    #lastReplaceViewportLogAt = 0;
    #lastStateTransitionLogAt = 0;
    #queryObserver: QueryObserver<any, Error, RowDataSourceQueryResult<T>, RowDataSourceQueryKey>;
    #disposableStack = new DisposableStack();

    constructor(config: RowDataSourceConstructionOptions<T, U, V>) {
        this.config = config;
        this.state.value = {
            ...this.state.value,
            rowHeight: config.rowHeight,
            topBuffer: config.bufferSize,
            bottomBuffer: config.bufferSize,
            initialFetchParameter: config.initialFetchParameter ?? null,
        };

        // Initialize the query observer. Note that this won't be in use until the query options factory indicates a fetch is needed.
        this.#queryObserver = new QueryObserver<any, Error, RowDataSourceQueryResult<T>, RowDataSourceQueryKey>(
            config.queryClient,
            {
                queryKey: ['ROW_DATA_SOURCE', 'OTHER', 'idle'],
                enabled: false,
            },
        );

        // Create a signal for query options that will be updated reactively
        const queryOptionsSignal = config.queryOptionsFactory(this.state);
        this.#disposableStack.defer(
            effect(() => {
                const options = queryOptionsSignal.value;
                if (TRACE) {
                    log.trace('Updating query observer options', { options });
                }
                this.#queryObserver.setOptions(options);
            }),
        );

        // Subscribe to query observer to handle fetch results
        this.#disposableStack.defer(
            this.#queryObserver.subscribe((result) => {
                const queryKey = this.#queryObserver.options.queryKey;
                const operation = queryKey[1]; // TypeScript knows this is the operation type

                // Handle fetch start (when fetching becomes true)
                if (result.isFetching && !result.data) {
                    switch (operation) {
                        case 'INITIAL_FETCH':
                            this.dispatch({ type: 'INITIAL_FETCH_START' });
                            break;
                        case 'FETCH_UP':
                            this.dispatch({ type: 'FETCH_UP_START' });
                            break;
                        case 'FETCH_DOWN':
                            this.dispatch({ type: 'FETCH_DOWN_START' });
                            break;
                    }
                }

                // Handle successful fetch
                if (result.data) {
                    this.dispatchQueryResult(result.data);
                }

                // Handle errors
                if (result.error) {
                    switch (operation) {
                        case 'INITIAL_FETCH':
                            this.dispatch({ type: 'INITIAL_FETCH_FAILURE' });
                            break;
                        case 'FETCH_UP':
                            this.dispatch({ type: 'FETCH_UP_FAILURE' });
                            break;
                        case 'FETCH_DOWN':
                            this.dispatch({ type: 'FETCH_DOWN_FAILURE' });
                            break;
                    }
                    log.error('Query failed', { error: result.error, queryKey });
                }
            }),
        );

        // Create debounced function in constructor after config is set
        // Use leading: true to ensure initial viewport update happens immediately
        this.updateViewport = debounce(
            (scrollTop: number, clientHeight: number) => {
                this.dispatch({ type: 'VIEWPORT_CHANGED', scrollTop, clientHeight });
            },
            config.viewportDebounceDelay,
            { leading: true, trailing: true },
        );
    }

    private dispatch(action: Action<T>) {
        if (TRACE) {
            log.trace(`Dispatching action [${action.type}]`);
        }
        const oldState = this.state.value;
        const newState = reducer(oldState, action, this.config);

        this.state.value = newState;

        if (action.type !== 'VIEWPORT_CHANGED') {
            const dataCountBefore = oldState.data.filter((r) => r.type === 'data').length;
            const dataCountAfter = newState.data.filter((r) => r.type === 'data').length;
            const now = Date.now();
            if (now - this.#lastStateTransitionLogAt >= 1_000) {
                this.#lastStateTransitionLogAt = now;
                if (TRACE) {
                    log.trace('RowDataSource state transition', {
                        action: action.type,
                        dataCountBefore,
                        dataCountAfter,
                        topBuffer: newState.topBuffer,
                        bottomBuffer: newState.bottomBuffer,
                        hasReachedStart: newState.hasReachedStart,
                        hasReachedEnd: newState.hasReachedEnd,
                        viewportShift: newState.viewportShift,
                        clientHeight: newState.clientHeight,
                    });
                }
            }
            if (dataCountAfter === 0 && dataCountBefore > 0) {
                if (TRACE) {
                    log.trace('RowDataSource data dropped to zero', {
                        action: action.type,
                        dataCountBefore,
                        scrollTop: newState.scrollTop,
                        clientHeight: newState.clientHeight,
                    });
                }
            }
        }
    }

    dispatchQueryResult(result: RowDataSourceQueryResult<T>) {
        switch (result.type) {
            case 'INITIAL_FETCH_SUCCESS':
                this.dispatch({
                    type: 'INITIAL_FETCH_SUCCESS',
                    rows: result.rows,
                    isEndOfDataUpward: result.isEndOfDataUpward,
                    isEndOfDataDownward: result.isEndOfDataDownward,
                });
                break;
            case 'INITIAL_FETCH_FAILURE':
                this.dispatch({ type: 'INITIAL_FETCH_FAILURE' });
                break;
            case 'FETCH_UP_SUCCESS':
                this.dispatch({
                    type: 'FETCH_UP_SUCCESS',
                    rows: result.rows,
                    isEndOfData: result.isEndOfData,
                });
                break;
            case 'FETCH_UP_FAILURE':
                this.dispatch({ type: 'FETCH_UP_FAILURE' });
                break;
            case 'FETCH_DOWN_SUCCESS':
                this.dispatch({
                    type: 'FETCH_DOWN_SUCCESS',
                    rows: result.rows,
                    isEndOfData: result.isEndOfData,
                });
                break;
            case 'FETCH_DOWN_FAILURE':
                this.dispatch({ type: 'FETCH_DOWN_FAILURE' });
                break;
            case 'INSERT_ROWS':
                this.dispatch({ type: 'INSERT_ROWS', rows: result.rows });
                break;
            default:
                throw new UnreachableCaseError(result);
        }
    }

    #totalRows = computed(() => {
        const s = this.state.value;
        return s.topBuffer + s.data.length + s.bottomBuffer;
    });

    totalHeight: ReadonlySignal<number> = computed(() => {
        return this.#totalRows.value * this.state.value.rowHeight;
    });

    isAtTop: ReadonlySignal<boolean> = computed(() => {
        const s = this.state.value;
        if (s.data.length === 0) {
            return true;
        }
        if (!s.hasReachedStart) {
            return false;
        }
        if (s.scrollTop <= s.topBuffer * s.rowHeight) {
            return true;
        }
        return false;
    });

    isFetchingUp: ReadonlySignal<boolean> = computed(() => {
        return this.state.value.isFetchingUp;
    });

    isFetchingDown: ReadonlySignal<boolean> = computed(() => {
        return this.state.value.isFetchingDown;
    });

    rows: ReadonlySignal<Row<T, U>[]> = computed(() => {
        const s = this.state.value;
        const rows: Row<T, U>[] = [];

        for (let i = 0; i < s.topBuffer; i++) {
            rows.push({
                type: 'buffer',
                id: `buffer-top-${i}`,
                position: 'top',
            });
        }

        rows.push(...s.data);

        for (let i = 0; i < s.bottomBuffer; i++) {
            rows.push({
                type: 'buffer',
                id: `buffer-bottom-${i}`,
                position: 'bottom',
            });
        }

        return rows;
    });

    setContainer(container: HTMLDivElement) {
        // Clean up old ResizeObserver if it exists
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        this.container = container;
        this.initializeDOM();
    }

    private initializeDOM() {
        if (!this.container) return;

        this.setupReactiveEffects();

        // Create ResizeObserver to detect when container gets a real height
        // This fixes the clientHeight: 0 bug when the container is initially hidden or not yet laid out
        this.resizeObserver = new ResizeObserver(() => {
            if (this.container && this.container.clientHeight > 0) {
                this.updateViewport(this.container.scrollTop, this.container.clientHeight);
            }
        });

        this.resizeObserver.observe(this.container);

        this.dispatch({
            type: 'VIEWPORT_CHANGED',
            scrollTop: 0,
            clientHeight: this.container.clientHeight,
        });

        if (this.container.clientHeight === 0) {
            log.warn('RowDataSource initialized with clientHeight: 0 — initial fetch blocked until layout', {
                containerOffsetHeight: this.container.offsetHeight,
            });
        }
    }

    private setupReactiveEffects() {
        // DisposableBox.next() disposes the previous subscription before creating
        // the new one, preventing leaks when setContainer is called multiple times
        // (e.g. after a data-source recreation triggers a new React callback ref).
        this.#reactiveEffectsBox.next(() =>
            toDisposable(
                this.state.subscribe((state) => {
                    if (state.viewportShift !== 0 && this.container) {
                        /*
                        if (TRACE) {
                            log.trace('Applying viewport shift', {
                                viewportShift: state.viewportShift,
                                currentScrollTop: this.container.scrollTop,
                                newScrollTop: this.container.scrollTop + state.viewportShift,
                            });
                        }
                        */
                        this.container.scrollTop += state.viewportShift;
                        this.dispatch({
                            type: 'VIEWPORT_CHANGED',
                            scrollTop: this.container.scrollTop,
                            clientHeight: this.container.clientHeight,
                        });
                    }
                }),
            ),
        );
    }

    /**
     * @deprecated This was being used to fetch live data, use `enqueueManualFetchUp` instead.
     */
    resetEndState(direction?: 'up' | 'down' | 'both') {
        if (TRACE) {
            log.trace('resetEndState called', { direction, hasContainer: !!this.container });
        }
        this.dispatch({ type: 'RESET_END_STATE', direction });
    }

    insertRows(rows: T[]) {
        this.dispatch({ type: 'INSERT_ROWS', rows });
    }

    /**
     * Upsert rows into the data source. For each row in rowsToUpsert:
     * - If a row with matching ID exists, the updater function is called with both the new and existing row
     * - If no row with matching ID exists, the updater function is called with the new row and undefined
     * - The updater's return value becomes the final row stored
     *
     * This allows for flexible update strategies:
     * - Simple replace: `(newRow, existing) => newRow`
     * - Merge: `(newRow, existing) => ({ ...existing, ...newRow })`
     * - Conditional: `(newRow, existing) => existing?.version > newRow.version ? existing : newRow`
     *
     * The upsert maintains:
     * - Sort order (via the comparator)
     * - Scroll position stability (via viewport shift calculation)
     * - End markers (automatically relocated if rows change position)
     * - Decorations (regenerated after the upsert)
     *
     * @param rowsToUpsert - Array of rows to upsert (identified by their id field)
     * @param updater - Function that determines the final row value given the new row and existing row (or undefined)
     *
     * @example
     * // Replace existing row or insert new one
     * dataSource.upsertRows([{ id: '1', ...newData }], (newRow) => newRow);
     *
     * @example
     * // Merge with existing data
     * dataSource.upsertRows(updates, (newRow, existing) => ({
     *     ...existing,
     *     ...newRow,
     *     updatedAt: Date.now()
     * }));
     *
     * @example
     * // Conditional update based on version
     * dataSource.upsertRows(maybeUpdates, (newRow, existing) => {
     *     if (!existing || newRow.version > existing.version) return newRow;
     *     return existing;
     * });
     */
    upsertRows(rowsToUpsert: T[], updater: (newRow: T, existingRow: T | undefined) => T) {
        this.dispatch({ type: 'UPSERT_ROWS', rows: rowsToUpsert, updater });
    }

    removeRowsById(rowIds: string[]) {
        this.dispatch({ type: 'REMOVE_ROWS_BY_ID', rowIds });
    }

    /**
     * Replace all current viewport rows with a fresh set of rows from a query.
     * This is useful for socket updates where we want to fully refresh the viewport
     * to catch removed rows, repositioned rows, etc.
     *
     * Note: This does full replacement without equality checking. Future optimization
     * opportunity: add isEqual checks to detect unchanged rows and preserve references.
     *
     * @param freshRows - The new rows to display
     * @param isEndOfDataUpward - Whether we've reached the start of the dataset
     * @param isEndOfDataDownward - Whether we've reached the end of the dataset
     */
    replaceViewportRows(freshRows: T[], isEndOfDataUpward: boolean, isEndOfDataDownward: boolean) {
        if (TRACE) {
            log.trace('replaceViewportRows called', {
                freshRowCount: freshRows.length,
                currentRowCount: this.state.value.data.length,
                isEndOfDataUpward,
                isEndOfDataDownward,
            });
        }

        this.dispatch({
            type: 'REPLACE_ROWS',
            rows: freshRows,
            isEndOfDataUpward,
            isEndOfDataDownward,
        });

        const finalState = this.state.value;
        const finalDataCount = finalState.data.filter((r) => r.type === 'data').length;

        const now = Date.now();
        if (now - this.#lastReplaceViewportLogAt >= 1_000) {
            this.#lastReplaceViewportLogAt = now;
            if (TRACE) {
                log.trace('replaceViewportRows completed', {
                    freshRowsProvided: freshRows.length,
                    finalDataCount,
                    isEndOfDataUpward,
                    isEndOfDataDownward,
                    clientHeight: finalState.clientHeight,
                    viewportShift: finalState.viewportShift,
                });
            }
        }
        if (finalDataCount === 0 && freshRows.length > 0) {
            log.warn('replaceViewportRows resulted in zero data despite receiving rows', {
                freshRowsProvided: freshRows.length,
                clientHeight: finalState.clientHeight,
                scrollTop: finalState.scrollTop,
            });
        }
    }

    enqueueManualFetchUp() {
        this.dispatch({ type: 'ENQUEUE_MANUAL_FETCH_UP' });
    }

    get rowHeight(): number {
        return this.config.rowHeight;
    }

    [Symbol.dispose]() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.#reactiveEffectsBox[Symbol.dispose]();
        this.#disposableStack.dispose();
        this.#queryObserver.destroy();
    }
}

export interface InfiniteTimeViewProps<T extends DataRow = DataRow, U extends DecorationRow = DecorationRow> {
    className?: string;
    dataSource: RowDataSource<T, U>;
    listComponent: any; // Svelte component type
    listComponentProps?: Record<string, any>; // Additional props to pass to the Svelte component
    header?: React.ReactElement; // Optional header element to render above the scrollable list
    onReturnToTop?: () => void; // Optional callback to handle return to top
}

export function InfiniteTimeView<T extends DataRow = DataRow, U extends DecorationRow = DecorationRow>({
    className,
    dataSource,
    listComponent,
    listComponentProps = EmptyObject,
    header,
    onReturnToTop,
}: InfiniteTimeViewProps<T, U>) {
    useSignals();
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const svelteComponentProps = useMemo(
        () => ({
            ...listComponentProps,
            dataSource,
        }),
        [dataSource, listComponentProps],
    );

    const svelteRef = useSvelteComponent(
        listComponent,
        useMemo(() => svelteComponentProps, [svelteComponentProps]),
    );

    const callbackRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (node !== null) {
                // Store the ref
                scrollContainerRef.current = node;
                // Set the container reference for the data source
                svelteRef(node);
                dataSource.setContainer(node);
            }
        },
        [svelteRef, dataSource],
    );

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const target = e.currentTarget;
            dataSource.updateViewport(target.scrollTop, target.clientHeight);
        },
        [dataSource],
    );

    useEffect(() => {
        // Set scroll to top automatically when data source changes
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [dataSource]);

    const content = (
        <div className="relative h-full w-full">
            <div
                ref={callbackRef}
                // Scrollbar hidden as it is jumpy and dragging it doesn't make for great UX. Consider un-hiding for debugging.
                className={cn(
                    'scrollbar-hide relative isolate h-full w-full overflow-auto will-change-scroll contain-content',
                    className,
                )}
                style={{ overflowAnchor: 'none' }}
                onScroll={handleScroll}
            />
            {!dataSource.isAtTop.value && onReturnToTop && (
                <button
                    type="button"
                    className="absolute top-2 right-2 z-10 rounded bg-gray-800 px-3 py-1 text-sm text-white shadow-md hover:bg-gray-700"
                    onClick={() => {
                        if (
                            dataSource.rows.value.some(
                                (row) => row && row.type === 'data' && row.isEndOfDataUpward === true,
                            ) &&
                            scrollContainerRef.current
                        ) {
                            // Scroll to top
                            scrollContainerRef.current.scrollTop = 0;
                            dataSource.updateViewport(0, scrollContainerRef.current.clientHeight);
                        } else {
                            // Call the callback to reset the data source
                            onReturnToTop();
                        }
                    }}
                >
                    Return to top
                </button>
            )}
        </div>
    );

    if (header) {
        return (
            <div className="flex h-full w-full flex-col">
                <div className="shrink-0">{header}</div>
                <div className="min-h-0 flex-1">{content}</div>
            </div>
        );
    }

    return content;
}
